import { createColumnHelper } from '@tanstack/react-table';
import React, { useState } from 'react';

import { ClientShortProfile, PersonOrgType } from '@ndvr/customer-api';

import { PageContent } from '@/components/ui/page-content';
import { formatDate, getHouseholdName, getOrgName, getPersonName, useHouseholdRoutePaths } from 'utils';
import { useHouseholdMap } from 'views/household/household-queries';
import { CustomTablePage, HouseholdLogoCard, PersonOrgLogoCard, Text, VerticalLayout } from 'widgets';

import { useClientShortProfilesForTenants } from './client-queries';
import { ClientDetailsDrawer } from './ClientDetailsDrawer';
import { ClientFiltersForm } from './ClientFilters/ClientFiltersForm';
import { useClientFilters } from './ClientFilters/ClientFiltersHook';

const columnHelper = createColumnHelper<ClientShortProfile>();

// Content component without wrapper - can be reused with custom header
export const ClientsListContent = ({
    centerContent,
    exportPortalId,
    rightActions,
}: {
    centerContent?: React.ReactNode;
    exportPortalId?: string;
    rightActions?: React.ReactNode;
}) => {
    const { OwnersTabRoutePath, PortfolioOverviewTabRoutePath } = useHouseholdRoutePaths();
    const { data: clientProfiles, isLoading } = useClientShortProfilesForTenants();
    const { data: filteredClientProfiles } = useClientFilters(clientProfiles ?? []);
    const [selectedClientProfile, setSelectedClientProfile] = useState<null | ClientShortProfile>(null);

    return (
        <>
            <ClientFiltersForm
                centerContent={centerContent}
                count={isLoading ? undefined : filteredClientProfiles.length}
                rightActions={rightActions}
            />

            {selectedClientProfile ? (
                <ClientDetailsDrawer
                    clientShortProfile={selectedClientProfile}
                    onClose={() => setSelectedClientProfile(null)}
                />
            ) : null}

            <PageContent scroll="children" padding="none">
                <CustomTablePage
                    rowOnClick={(client) => setSelectedClientProfile(client)}
                    columns={createColumns({ OwnersTabRoutePath, PortfolioOverviewTabRoutePath })}
                    data={filteredClientProfiles}
                    defaultSortCol="name"
                    isLoading={isLoading}
                    exportName="account_owners"
                    exportPortalId={exportPortalId}
                    cardClassName="m-0 border-0 shadow-none"
                />
            </PageContent>
        </>
    );
};

const createColumns = ({
    OwnersTabRoutePath,
    PortfolioOverviewTabRoutePath,
}: Pick<ReturnType<typeof useHouseholdRoutePaths>, 'OwnersTabRoutePath' | 'PortfolioOverviewTabRoutePath'>) => [
    columnHelper.accessor(
        (data) => (data.personOrgType === PersonOrgType.PERSON ? getPersonName(data) : getOrgName(data)),
        {
            id: 'name',
            header: 'Name',
            cell: ({ getValue, row }) => (
                <PersonOrgLogoCard
                    name={getValue()}
                    personOrgType={row.original.personOrgType}
                    to={OwnersTabRoutePath(row.original.globalAccountGUID)}
                />
            ),
            sortingFn: 'alphanumeric',
        },
    ),
    columnHelper.accessor((data) => data.primaryAddress?.state, {
        id: 'state',
        header: 'State',
        cell: ({ row }) => {
            return (
                <VerticalLayout $gap={4}>
                    <Text $truncated>{row.original.primaryAddress?.state}</Text>
                    <Text $truncated helper>
                        {row.original.primaryAddress?.postalCode?.plainText}
                    </Text>
                </VerticalLayout>
            );
        },
        enableSorting: false,
        size: 150,
    }),
    columnHelper.accessor('startDate', {
        id: 'startDate',
        header: 'Start Date',
        cell: ({ getValue }) => <Text>{formatDate(getValue())}</Text>,
        sortingFn: 'datetime',
        size: 150,
    }),
    columnHelper.accessor('globalAccountGUID', {
        id: 'globalAccountGUID',
        header: 'household name',
        cell: ({ getValue }) => (
            <HouseholdCell
                globalAccountGUID={getValue()}
                PortfolioOverviewTabRoutePath={PortfolioOverviewTabRoutePath}
            />
        ),
        enableSorting: false,
    }),
];

const HouseholdCell = ({
    globalAccountGUID,
    PortfolioOverviewTabRoutePath,
}: {
    globalAccountGUID: string;
    PortfolioOverviewTabRoutePath: ReturnType<typeof useHouseholdRoutePaths>['PortfolioOverviewTabRoutePath'];
}) => {
    const householdMap = useHouseholdMap();
    const account = householdMap.get(globalAccountGUID);
    return account ? (
        <HouseholdLogoCard name={getHouseholdName(account)} to={PortfolioOverviewTabRoutePath(globalAccountGUID)} />
    ) : null;
};
